import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Container, Row, Col, Form, Button, Alert, Spinner, Nav, Card, Image,
} from "react-bootstrap";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { SchemeSearchPublic } from "../Components/Intake/SchemeSearchPublic";
import { PlanOptionSelectorPublic } from "../Components/Intake/PlanOptionSelectorPublic";
import { getIntakeSession, submitIntakeForm, checkIntakePreflight } from "../intakeApi";
import { generateOzowLink } from "../afinanceapi";
import NoteRenderer from "../Components/Templates/NoteRenderer";
import "./home.css";

const LANGUAGES = ["English", "Afrikaans", "Zulu", "Xhosa", "Sotho", "Tswana", "Pedi", "Venda", "Tsonga", "Ndebele", "Swati"];
const GENDERS = [{ value: "1", label: "Male" }, { value: "2", label: "Female" }, { value: "3", label: "Other" }];

// Legacy step number for each step kind. Used to keep the existing `step === N`
// JSX conditionals working unchanged. Template-page steps get 0 (no legacy match).
const KIND_LEGACY = {
  "patient-type":    1,
  "medical-aid":     2,
  "patient-details": 3,
  "account-holder":  4,
  "review":          5,
};

export function IntakeForm({
  tokenIdProp  = null,
  rawTokenProp = null,
  embedded     = false,
  onCompleted  = null,
}) {
  const params   = useParams();
  const location = useLocation();
  const tokenId  = tokenIdProp  ?? params.tokenId;
  const rawToken = rawTokenProp ?? new URLSearchParams(location.search).get("t") ?? "";
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [sessionState, setSessionState] = useState("loading"); // loading | valid | invalid | submitted
  const [practiceName, setPracticeName] = useState("");
  const [practiceLogo, setPracticeLogo] = useState(null);
  const [captchaRequired, setCaptchaRequired] = useState(false);

  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Intake templates snapshot received from the server (frozen at link-send time).
  // Each template's pages become consecutive steps inserted before the review step.
  const [intakeTemplates, setIntakeTemplates] = useState([]);
  // { [templateId]: { capturedValues: { [elementId]: value } } }
  const [templateCaptures, setTemplateCaptures] = useState({});

  // Step 1: flow selection
  const [patientStatus, setPatientStatus] = useState(""); // NEW | EXISTING
  const [payerType, setPayerType] = useState("");          // MEDICAL_AID | PRIVATE

  // Step 2: medical aid details
  const [scheme, setScheme] = useState(null);
  const [planOption, setPlanOption] = useState(null);
  const [medicalAidNumber, setMedicalAidNumber] = useState("");
  const [dependantCode, setDependantCode] = useState("");

  // Step 3: patient details
  const [patient, setPatient] = useState({
    title: "", firstName: "", lastName: "", initials: "", fullNames: "",
    idNumber: "", dob: "", gender: "", tel: "", email: "", language: "",
  });

  // Step 4: account holder / identifiers
  const [responsibleForAccount, setResponsibleForAccount] = useState(true);
  const [accountHolder, setAccountHolder] = useState({
    title: "", firstName: "", lastName: "", initials: "", fullNames: "",
    idNumber: "", dob: "", tel: "", email: "", language: "",
    address1: "", address2: "", city: "", province: "", postalCode: "",
  });
  // Flow C/D: main member identifiers
  const [mainMember, setMainMember] = useState({
    firstName: "", lastName: "", idNumber: "", dob: "", tel: "", email: "",
  });

  // Online booking context (present when arrived from onlinebooking.jsx)
  const [bookingContext, setBookingContext] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);

  // True when session was created from a practice appointment and link sent via email
  const [isAppointmentLinked, setIsAppointmentLinked] = useState(false);

  // Flow A preflight
  const [formMode, setFormMode] = useState("");       // "" | "brief" | "full"
  const [preflightLoading, setPreflightLoading] = useState(false);
  const [isMainMember, setIsMainMember] = useState(true); // step 4 Flow A full mode
  const [termsAndConditions, setTermsAndConditions] = useState(null);
  const [consentAgreed, setConsentAgreed] = useState(false);
  const [isProxy, setIsProxy] = useState(false);
  const [proxyNote, setProxyNote] = useState("");
  const [contact, setContact] = useState({            // brief mode shared contact
    email: "", tel: "", address1: "", address2: "",
    city: "", province: "", postalCode: "", language: "",
  });

  // Push reCAPTCHA badge above the fixed footer while this page is mounted
  useEffect(() => {
    if (embedded) return;
    const style = document.createElement("style");
    style.textContent = ".grecaptcha-badge { bottom: 66px !important; }";
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, [embedded]);

  // Validate session on mount
  useEffect(() => {
    if (!tokenId || !rawToken) { setSessionState("invalid"); return; }
    getIntakeSession(tokenId, rawToken).then((data) => {
      if (data.valid) {
        setSessionState("valid");
        setPracticeName(data.practiceName || "");
        setPracticeLogo(data.practiceLogo || null);
        setCaptchaRequired(data.captchaRequired || false);
        setTermsAndConditions(data.termsAndConditions || null);
        setBookingContext(data.bookingContext || null);
        setIntakeTemplates(Array.isArray(data.intakeTemplates) ? data.intakeTemplates : []);
        if (data.isAppointmentLinked) {
          setIsAppointmentLinked(true);
          setPatientStatus("NEW");
        }
      } else {
        setSessionState(data.expired ? "expired" : data.locked ? "locked" : "invalid");
      }
    });
  }, [tokenId, rawToken]);

  // Derived: which flow key
  const flowKey =
    patientStatus === "NEW" && payerType === "MEDICAL_AID" ? "A"
    : patientStatus === "NEW" && payerType === "PRIVATE" ? "B"
    : patientStatus === "EXISTING" && payerType === "MEDICAL_AID" ? "C"
    : patientStatus === "EXISTING" && payerType === "PRIVATE" ? "D"
    : null;

  // Dynamic step model — data-driven array of step descriptors.
  // Intake-template pages (one step per page per template) sit between
  // "account-holder" and "review" so T&C remains the last step in every flow.
  const steps = useMemo(() => {
    const out = [];
    out.push({ kind: "patient-type",    label: "Patient Type" });
    if (payerType === "MEDICAL_AID") {
      out.push({ kind: "medical-aid",   label: "Medical Aid" });
    }
    out.push({ kind: "patient-details", label: "Patient Details" });
    // Brief mode (Flow A FC found dependants) skips the account-holder step —
    // contact fields are collected on patient-details instead.
    const skipAccountHolder = patientStatus === "NEW" && payerType === "MEDICAL_AID" && formMode === "brief";
    if (!skipAccountHolder) {
      out.push({
        kind: "account-holder",
        label: patientStatus === "EXISTING" ? "Identification" : "Account Holder",
      });
    }
    (intakeTemplates || []).forEach((tpl, ti) => {
      (tpl.pages || []).forEach((page, pi) => {
        out.push({
          kind: "template-page",
          label: page.pageTitle || tpl.templateTitle || `Template ${ti + 1}`,
          templateIndex: ti,
          pageIndex: pi,
          templateId: tpl.templateId,
          templateVersion: tpl.templateVersion,
          page,
        });
      });
    });
    out.push({ kind: "review", label: "Review & Submit" });
    return out;
  }, [patientStatus, payerType, formMode, intakeTemplates]);

  // Clamp stepIndex when the steps array shrinks (e.g. switch to PRIVATE removes medical-aid).
  useEffect(() => {
    if (stepIndex > steps.length - 1) setStepIndex(steps.length - 1);
  }, [steps.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Legacy step number derived from current kind — keeps existing `step === N`
  // JSX conditionals working without rewriting every card. Template-page steps
  // get 0 so none of those conditions match (they have their own render block).
  const currentKind = steps[stepIndex]?.kind;
  const step = KIND_LEGACY[currentKind] ?? 0;

  async function goNext() {
    const errs = validateStepByKind(currentKind);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    // Flow A preflight: run between medical-aid and patient-details (discreet — no UI message about result)
    if (currentKind === "medical-aid" && patientStatus === "NEW" && payerType === "MEDICAL_AID") {
      setPreflightLoading(true);
      const result = await checkIntakePreflight(tokenId, rawToken, {
        medicalAidNumber,
        optionCode: planOption?.option?.code || "",
      });
      setFormMode(result.short ? "brief" : "full");
      setPreflightLoading(false);
    }
    setStepIndex((i) => Math.min(i + 1, steps.length - 1));
  }

  function goBack() {
    setErrors({});
    setStepIndex((i) => Math.max(i - 1, 0));
  }

  function validateStepByKind(kind) {
    const errs = {};
    if (kind === "patient-type") {
      if (!patientStatus) errs.patientStatus = "Please select an option.";
      if (!payerType) errs.payerType = "Please select an option.";
    }
    if (kind === "medical-aid") {
      if (!scheme) errs.scheme = "Please select a medical aid.";
      if (!planOption) errs.planOption = "Please select a plan / option.";
      if (!medicalAidNumber.trim()) errs.medicalAidNumber = "Medical aid number is required.";
    }
    if (kind === "patient-details") {
      // Brief mode (Flow A, FC found): only contact fields collected here
      if (patientStatus === "NEW" && payerType === "MEDICAL_AID" && formMode === "brief") {
        if (!contact.tel.trim()) errs["contact.tel"] = "Phone number is required.";
        return errs;
      }
      if (!patient.firstName.trim()) errs["patient.firstName"] = "First name is required.";
      if (!patient.lastName.trim()) errs["patient.lastName"] = "Last name is required.";
      if (!patient.dob) errs["patient.dob"] = "Date of birth is required.";
      if (!patient.tel.trim()) errs["patient.tel"] = "Mobile number is required.";
    }
    if (kind === "account-holder") {
      if (payerType === "MEDICAL_AID") {
        // Flow A: account holder (main member)
        if (flowKey === "A") {
          if (isMainMember) {
            // Abbreviated contact only
            if (!accountHolder.tel.trim()) errs["accountHolder.tel"] = "Phone number is required.";
          } else {
            // Full account holder form
            if (!accountHolder.firstName.trim()) errs["accountHolder.firstName"] = "First name is required.";
            if (!accountHolder.lastName.trim()) errs["accountHolder.lastName"] = "Last name is required.";
            if (!accountHolder.tel.trim()) errs["accountHolder.tel"] = "Phone number is required.";
          }
        }
        // Flow C: main member identifiers
        if (flowKey === "C") {
          if (!mainMember.firstName.trim()) errs["mainMember.firstName"] = "First name is required.";
          if (!mainMember.lastName.trim()) errs["mainMember.lastName"] = "Last name is required.";
        }
      }
      if (flowKey === "B" && !responsibleForAccount) {
        if (!accountHolder.firstName.trim()) errs["accountHolder.firstName"] = "First name is required.";
        if (!accountHolder.lastName.trim()) errs["accountHolder.lastName"] = "Last name is required.";
        if (!accountHolder.tel.trim()) errs["accountHolder.tel"] = "Phone number is required.";
        if (!accountHolder.address1.trim()) errs["accountHolder.address1"] = "Home address is required.";
      }
      if (flowKey === "B" && responsibleForAccount) {
        if (!accountHolder.address1.trim()) errs["accountHolder.address1"] = "Home address is required.";
      }
      // Flow D: account holder identifiers
      if (flowKey === "D") {
        if (!accountHolder.firstName.trim()) errs["accountHolder.firstName"] = "First name is required.";
        if (!accountHolder.lastName.trim()) errs["accountHolder.lastName"] = "Last name is required.";
        if (!accountHolder.dob) errs["accountHolder.dob"] = "Date of birth is required.";
      }
    }
    return errs;
  }

  const handleSubmit = useCallback(async () => {
    // Defensive: re-validate the data-entry step preceding review. Each step's
    // goNext() already validates on forward navigation, so this is a safety net.
    const lastDataKind = (patientStatus === "NEW" && payerType === "MEDICAL_AID" && formMode === "brief")
      ? "patient-details"
      : "account-holder";
    const errs = validateStepByKind(lastDataKind);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setSubmitting(true);

    let recaptchaToken = null;
    if (captchaRequired && executeRecaptcha) {
      try { recaptchaToken = await executeRecaptcha("intake_submit"); } catch { /* skip */ }
    }

    const payload = buildPayload();
    // Attach intake-template captures in the order they were snapshotted on the session.
    payload.intakeTemplates = (intakeTemplates || []).map((t) => ({
      templateId:      t.templateId,
      templateVersion: t.templateVersion,
      capturedValues:  templateCaptures[t.templateId]?.capturedValues || {},
    }));
    const response = await submitIntakeForm(
      tokenId,
      rawToken,
      { patientStatus, payerType },
      payload,
      recaptchaToken
    );

    // Online booking: handle appointment creation response + optional payment redirect
    if (response?.booking) {
      setBookingResult(response.booking);
      if (response.booking.requiresPayment) {
        try {
          const ozowRes = await generateOzowLink({
            practiceNumber:       response.booking.practiceNumber,
            totalSelectedAmount:  response.booking.amount,
            accountLastName:      `${response.booking.patientLastName} ${response.booking.patientFirstName}`,
            transactionRef:       response.booking.transactionRef,
          });
          if (ozowRes?.formData && ozowRes?.actionUrl) {
            const form = document.createElement("form");
            form.method = "POST";
            form.action = ozowRes.actionUrl;
            form.style.display = "none";
            Object.keys(ozowRes.formData).forEach((key) => {
              const input = document.createElement("input");
              input.type = "hidden"; input.name = key; input.value = ozowRes.formData[key];
              form.appendChild(input);
            });
            document.body.appendChild(form);
            form.submit();
            return; // browser redirects to Ozow
          }
        } catch { /* non-fatal — fall through to confirmation screen */ }
      }
    }

    setSubmitting(false);
    if (onCompleted) {
      onCompleted(response?.booking ?? null);
    } else {
      setSessionState("submitted");
    }
  }, [captchaRequired, executeRecaptcha, tokenId, rawToken, patientStatus, payerType,
      scheme, planOption, medicalAidNumber, dependantCode, patient, formMode,
      responsibleForAccount, accountHolder, mainMember, contact, isMainMember,
      termsAndConditions, consentAgreed, isProxy, proxyNote,
      bookingContext, onCompleted,
      intakeTemplates, templateCaptures]); // eslint-disable-line react-hooks/exhaustive-deps

  function buildPayload() {
    const medicalAidObj = scheme ? {
      schemeName: scheme.name,
      schemeCode: scheme.code,
      planName: planOption?.plan?.plan || "",
      planCode: planOption?.plan?.code || "",
      optionName: planOption?.option?.option || "",
      optionCode: planOption?.option?.code || "",
      medicalAidNumber: medicalAidNumber.trim(),
      dependantCode: dependantCode.trim(),
    } : null;

    const consentObj = termsAndConditions ? {
      agreed: consentAgreed,
      isProxy,
      proxyNote: isProxy ? proxyNote : "",
    } : null;

    // Flow A: payload varies by formMode
    if (patientStatus === "NEW" && payerType === "MEDICAL_AID") {
      if (formMode === "brief") {
        return { medicalAid: medicalAidObj, flowMode: "brief", contact: { ...contact }, consent: consentObj };
      }
      return {
        medicalAid: medicalAidObj,
        flowMode: isMainMember ? "full_main" : "full",
        patient: { ...patient },
        accountHolder: { ...accountHolder },
        isMainMember,
        consent: consentObj,
      };
    }

    // When the patient is the account holder, merge their identity onto the
    // accountHolder object so the backend has both name AND address in one
    // place. Filter out empty patient fields BEFORE the spread — otherwise an
    // empty `patient.title` / `patient.language` would silently clobber a
    // value the user typed into the accountHolder section.
    const patientNonEmpty = Object.fromEntries(
      Object.entries(patient).filter(([_, v]) => v !== "" && v != null),
    );
    return {
      medicalAid: medicalAidObj,
      patient: { ...patient },
      accountHolder: responsibleForAccount
        ? { ...accountHolder, ...patientNonEmpty }
        : { ...accountHolder },
      mainMember: { ...mainMember },
      responsibleForAccount,
      consent: consentObj,
    };
  }

  // Helpers for controlled inputs
  const pField = (key) => ({
    value: patient[key],
    onChange: (e) => setPatient((p) => ({ ...p, [key]: e.target.value })),
    isInvalid: !!errors[`patient.${key}`],
  });

  const ahField = (key) => ({
    value: accountHolder[key],
    onChange: (e) => setAccountHolder((a) => ({ ...a, [key]: e.target.value })),
    isInvalid: !!errors[`accountHolder.${key}`],
  });

  const mmField = (key) => ({
    value: mainMember[key],
    onChange: (e) => setMainMember((m) => ({ ...m, [key]: e.target.value })),
    isInvalid: !!errors[`mainMember.${key}`],
  });

  const ctField = (key) => ({
    value: contact[key],
    onChange: (e) => setContact((c) => ({ ...c, [key]: e.target.value })),
    isInvalid: !!errors[`contact.${key}`],
  });

  // ---------------------------------------------------------------------------
  // Render states
  // ---------------------------------------------------------------------------
  if (sessionState === "loading") {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  if (sessionState === "submitted") {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Card className="text-center shadow" style={{ maxWidth: 480, width: "100%" }}>
          <Card.Body className="p-5">
            <div style={{ fontSize: 48, marginBottom: 16 }}>✓</div>
            {bookingContext ? (
              <>
                <Card.Title>Appointment Confirmed!</Card.Title>
                <Card.Text className="text-muted">
                  Your appointment has been booked successfully.
                </Card.Text>
                <div className="border rounded p-3 text-start small mt-3">
                  <div><strong>Service:</strong> {bookingContext.serviceName}</div>
                  <div><strong>Practitioner:</strong> {bookingContext.practitionerName}</div>
                  <div>
                    <strong>Date &amp; Time:</strong>{" "}
                    {new Date(bookingContext.start).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}
                  </div>
                  <div><strong>Practice:</strong> {bookingContext.practiceName}</div>
                  {bookingContext.locationName && (
                    <div><strong>Location:</strong> {bookingContext.locationName}</div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Card.Title>Details Submitted</Card.Title>
                <Card.Text className="text-muted">
                  Thank you. Your details were submitted to the practice. They will be in touch with you shortly.
                </Card.Text>
              </>
            )}
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (["invalid", "expired", "locked"].includes(sessionState)) {
    const msg =
      sessionState === "expired"
        ? "This intake link has expired. Please contact the practice for a new link."
        : sessionState === "locked"
        ? "This intake link has been used already. Please contact the practice if you need to resubmit."
        : "This intake link is invalid or has already been used. Please contact the practice.";
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Card className="shadow" style={{ maxWidth: 480, width: "100%" }}>
          <Card.Body className="p-5 text-center">
            <Card.Title className="text-danger">Link Unavailable</Card.Title>
            <Card.Text className="text-muted">{msg}</Card.Text>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // ---------------------------------------------------------------------------
  // Main wizard
  // ---------------------------------------------------------------------------
  return (
    <div style={embedded ? {} : { minHeight: "100vh", backgroundColor: "#f8f9fa", paddingBottom: 56 }}>
      {/* Header */}
      {!embedded && (
        <div className="bg-white border-bottom py-3 px-4">
          <Row className="g-3 align-items-center">
            <Col xs="auto">
              {practiceLogo
                ? <Image src={practiceLogo} alt="Practice Logo" style={{ maxHeight: 52 }} fluid />
                : <div style={{ width: 52 }} />}
            </Col>
            <Col className="text-center">
              <h2 className="mb-0 mt-0 text-primary fw-normal">{practiceName || "Patient Intake Form"}</h2>
              <div className="text-muted small mt-1">Secure Patient Intake Form</div>
            </Col>
            <Col xs="auto" style={{ visibility: "hidden" }}>
              <div style={{ width: 52 }} />
            </Col>
          </Row>
        </div>
      )}

      {/* Step indicator */}
      <div className="bg-white border-bottom px-3 py-3">
        <Nav variant="pills" className="justify-content-center step-indicator flex-nowrap mb-0">
          {steps.map((s, idx) => {
            let stepClass = "step-future";
            if (idx === stepIndex) stepClass = "active-step";
            else if (idx < stepIndex) stepClass = "step-completed";
            return (
              <Nav.Item key={`${s.kind}-${idx}`} className="d-flex align-items-center">
                <Nav.Link className={`step-link ${stepClass}`} disabled>
                  <span className="step-number me-1">{idx + 1}</span>
                  <span className="step-label">{s.label}</span>
                </Nav.Link>
                {idx < steps.length - 1 && (
                  <span className="step-connector mx-1">›</span>
                )}
              </Nav.Item>
            );
          })}
        </Nav>
      </div>

      <Container style={{ maxWidth: 640, paddingTop: 24 }}>
        <Form noValidate>

          {/* ---------------------------------------------------------------- */}
          {/* STEP 1: Patient type + payer type                                */}
          {/* ---------------------------------------------------------------- */}
          {step === 1 && (
            <Card className="shadow-sm mb-3">
              <Card.Body>
                <Card.Title className="mb-4">Tell us about the patient</Card.Title>
                {!isAppointmentLinked && (
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-primary">Are we booking a new or existing patient at this practice?</Form.Label>
                    <div className="d-flex gap-3 mt-2">
                      {[["NEW", "New patient"], ["EXISTING", "Existing patient"]].map(([val, lbl]) => (
                        <Button
                          key={val}
                          variant={patientStatus === val ? "primary" : "outline-primary"}
                          onClick={() => setPatientStatus(val)}
                          style={{ flex: 1, minHeight: 48 }}
                        >
                          {lbl}
                        </Button>
                      ))}
                    </div>
                    {errors.patientStatus && <div className="text-danger small mt-1">{errors.patientStatus}</div>}
                  </Form.Group>
                )}

                <Form.Group className="mb-2">
                  <Form.Label className="fw-semibold text-primary">Does the patient have a medical aid?</Form.Label>
                  <div className="d-flex gap-3 mt-2">
                    {[["MEDICAL_AID", "Yes, the patient has a medical aid"], ["PRIVATE", "No, the patient is a private / cash patient"]].map(([val, lbl]) => (
                      <Button
                        key={val}
                        variant={payerType === val ? "primary" : "outline-primary"}
                        onClick={() => setPayerType(val)}
                        style={{ flex: 1, minHeight: 48 }}
                      >
                        {lbl}
                      </Button>
                    ))}
                  </div>
                  {errors.payerType && <div className="text-danger small mt-1">{errors.payerType}</div>}
                </Form.Group>
              </Card.Body>
            </Card>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* STEP 2: Medical aid details (Flows A + C)                        */}
          {/* ---------------------------------------------------------------- */}
          {step === 2 && (
            <Card className="shadow-sm mb-3">
              <Card.Body>
                <Card.Title className="mb-4">Medical Aid Details</Card.Title>

                <Form.Group className="mb-3">
                  <Form.Label>Medical Aid <span className="text-danger">*</span></Form.Label>
                  <SchemeSearchPublic
                    value={scheme}
                    onChange={(s) => { setScheme(s); setPlanOption(null); }}
                    isInvalid={!!errors.scheme}
                  />
                  {errors.scheme && <div className="text-danger small mt-1">{errors.scheme}</div>}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Plan / Option <span className="text-danger">*</span></Form.Label>
                  <PlanOptionSelectorPublic
                    schemeCode={scheme?.code}
                    value={planOption}
                    onChange={setPlanOption}
                    isInvalid={!!errors.planOption}
                  />
                  {errors.planOption && <div className="text-danger small mt-1">{errors.planOption}</div>}
                </Form.Group>

                <Row className="g-3">
                  <Col xs={12} md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>Medical Aid Number <span className="text-danger">*</span></Form.Label>
                      <Form.Control
                        value={medicalAidNumber}
                        onChange={(e) => setMedicalAidNumber(e.target.value)}
                        isInvalid={!!errors.medicalAidNumber}
                        placeholder="e.g. MK1020533"
                      />
                      {errors.medicalAidNumber && (
                        <Form.Control.Feedback type="invalid">{errors.medicalAidNumber}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Dependant Code</Form.Label>
                      <Form.Control
                        value={dependantCode}
                        onChange={(e) => setDependantCode(e.target.value)}
                        placeholder="e.g. 01"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* STEP 3: Patient personal details (all flows)                    */}
          {/* ---------------------------------------------------------------- */}
          {step === 3 && patientStatus === "NEW" && payerType === "MEDICAL_AID" && formMode === "brief" && (
            <Card className="shadow-sm mb-3">
              <Card.Body>
                <Card.Title className="mb-1">Contact Details</Card.Title>
                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                      <Form.Control {...ctField("tel")} type="tel" placeholder="+27 82 123 4567" />
                      {errors["contact.tel"] && <div className="text-danger small mt-1">{errors["contact.tel"]}</div>}
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control {...ctField("email")} type="email" placeholder="email@example.com" />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Preferred Language</Form.Label>
                      <Form.Select {...ctField("language")}>
                        <option value=""></option>
                        {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Home Address</Form.Label>
                  <Form.Control {...ctField("address1")} placeholder="Address line 1" className="mb-2" />
                  <Form.Control {...ctField("address2")} placeholder="Address line 2 (optional)" className="mb-2" />
                  <Row className="g-3">
                    <Col xs={12} md={5}><Form.Control {...ctField("city")} placeholder="City" /></Col>
                    <Col xs={8} md={4}><Form.Control {...ctField("province")} placeholder="Province" /></Col>
                    <Col xs={4} md={3}><Form.Control {...ctField("postalCode")} placeholder="Postal code" /></Col>
                  </Row>
                </Form.Group>
              </Card.Body>
            </Card>
          )}
          {step === 3 && !(patientStatus === "NEW" && payerType === "MEDICAL_AID" && formMode === "brief") && (
            <Card className="shadow-sm mb-3">
              <Card.Body>
                <Card.Title className="mb-1">
                  {patientStatus === "EXISTING" ? "Patient Details (for identification)" : "Patient Details"}
                </Card.Title>
                {patientStatus === "EXISTING" && (
                  <p className="text-muted small mb-4">
                    These details will be used to locate your existing record. Please enter them exactly as registered.
                  </p>
                )}

                <Row className="g-3">
                  <Col xs={4} md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Title</Form.Label>
                      <Form.Select {...pField("title")}>
                        <option value=""></option>
                        {["Mr", "Mrs", "Ms", "Dr", "Prof"].map((t) => <option key={t}>{t}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={8} md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control {...pField("firstName")} placeholder="First name" />
                      {errors["patient.firstName"] && <div className="text-danger small mt-1">{errors["patient.firstName"]}</div>}
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control {...pField("lastName")} placeholder="Last name" />
                      {errors["patient.lastName"] && <div className="text-danger small mt-1">{errors["patient.lastName"]}</div>}
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="date" {...pField("dob")} />
                      {errors["patient.dob"] && <div className="text-danger small mt-1">{errors["patient.dob"]}</div>}
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ID Number</Form.Label>
                      <Form.Control {...pField("idNumber")} placeholder="13-digit SA ID" maxLength={13} />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Gender</Form.Label>
                      <Form.Select {...pField("gender")}>
                        <option value=""></option>
                        {GENDERS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Home Language</Form.Label>
                      <Form.Select {...pField("language")}>
                        <option value=""></option>
                        {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="g-3">
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Mobile Number <span className="text-danger">*</span></Form.Label>
                      <Form.Control {...pField("tel")} type="tel" placeholder="+27 82 123 4567" />
                      {errors["patient.tel"] && <div className="text-danger small mt-1">{errors["patient.tel"]}</div>}
                    </Form.Group>
                  </Col>
                  <Col xs={12} md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control {...pField("email")} type="email" placeholder="email@example.com" />
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* STEP 4: Account holder / identifiers                             */}
          {/* ---------------------------------------------------------------- */}
          {step === 4 && (
            <>
              {/* Flow A: main member / account holder details */}
              {flowKey === "A" && (
                <Card className="shadow-sm mb-3">
                  <Card.Body>
                    <Card.Title className="mb-3">Account Details</Card.Title>
                    <Form.Check
                      type="switch"
                      id="isMainMemberSwitch"
                      className="mb-4"
                      label="I am the main member on this medical aid"
                      checked={isMainMember}
                      onChange={(e) => setIsMainMember(e.target.checked)}
                    />
                    {isMainMember ? (
                      /* Abbreviated: contact info only (same fields as brief step 3) */
                      <>
                        <Row className="g-3">
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                              <Form.Control {...ahField("tel")} type="tel" />
                              {errors["accountHolder.tel"] && <div className="text-danger small mt-1">{errors["accountHolder.tel"]}</div>}
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Email Address</Form.Label>
                              <Form.Control {...ahField("email")} type="email" />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="g-3">
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Home Language</Form.Label>
                              <Form.Select {...ahField("language")}>
                                <option value=""></option>
                                {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-3">
                          <Form.Label>Home Address</Form.Label>
                          <Form.Control {...ahField("address1")} placeholder="Address line 1" className="mb-2" />
                          <Form.Control {...ahField("address2")} placeholder="Address line 2 (optional)" className="mb-2" />
                          <Row className="g-3">
                            <Col xs={12} md={5}><Form.Control {...ahField("city")} placeholder="City" /></Col>
                            <Col xs={8} md={4}><Form.Control {...ahField("province")} placeholder="Province" /></Col>
                            <Col xs={4} md={3}><Form.Control {...ahField("postalCode")} placeholder="Postal code" /></Col>
                          </Row>
                        </Form.Group>
                      </>
                    ) : (
                      /* Full account holder form */
                      <>
                        <Row className="g-3">
                          <Col xs={4} md={2}>
                            <Form.Group className="mb-3">
                              <Form.Label>Title</Form.Label>
                              <Form.Select {...ahField("title")}>
                                <option value=""></option>
                                {["Mr", "Mrs", "Ms", "Dr", "Prof"].map((t) => <option key={t}>{t}</option>)}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col xs={8} md={5}>
                            <Form.Group className="mb-3">
                              <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                              <Form.Control {...ahField("firstName")} placeholder="First name" />
                              {errors["accountHolder.firstName"] && <div className="text-danger small mt-1">{errors["accountHolder.firstName"]}</div>}
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={5}>
                            <Form.Group className="mb-3">
                              <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                              <Form.Control {...ahField("lastName")} placeholder="Last name" />
                              {errors["accountHolder.lastName"] && <div className="text-danger small mt-1">{errors["accountHolder.lastName"]}</div>}
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="g-3">
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                              <Form.Control {...ahField("tel")} type="tel" />
                              {errors["accountHolder.tel"] && <div className="text-danger small mt-1">{errors["accountHolder.tel"]}</div>}
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Email Address</Form.Label>
                              <Form.Control {...ahField("email")} type="email" />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="g-3">
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Home Language</Form.Label>
                              <Form.Select {...ahField("language")}>
                                <option value=""></option>
                                {LANGUAGES.map((l) => <option key={l}>{l}</option>)}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-3">
                          <Form.Label>Home Address</Form.Label>
                          <Form.Control {...ahField("address1")} placeholder="Address line 1" className="mb-2" />
                          <Form.Control {...ahField("address2")} placeholder="Address line 2 (optional)" className="mb-2" />
                          <Row className="g-3">
                            <Col xs={12} md={5}><Form.Control {...ahField("city")} placeholder="City" /></Col>
                            <Col xs={8} md={4}><Form.Control {...ahField("province")} placeholder="Province" /></Col>
                            <Col xs={4} md={3}><Form.Control {...ahField("postalCode")} placeholder="Postal code" /></Col>
                          </Row>
                        </Form.Group>
                      </>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Flow B: responsible for account + address */}
              {flowKey === "B" && (
                <Card className="shadow-sm mb-3">
                  <Card.Body>
                    <Card.Title className="mb-3">Account Details</Card.Title>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-semibold">Are you responsible for your own account?</Form.Label>
                      <div className="d-flex gap-3 mt-2">
                        {[[true, "Yes"], [false, "No, someone else is"]].map(([val, lbl]) => (
                          <Button
                            key={String(val)}
                            variant={responsibleForAccount === val ? "primary" : "outline-primary"}
                            onClick={() => setResponsibleForAccount(val)}
                            style={{ flex: 1, minHeight: 48 }}
                          >
                            {lbl}
                          </Button>
                        ))}
                      </div>
                    </Form.Group>

                    {!responsibleForAccount && (
                      <>
                        <p className="text-muted small">Please provide the account holder's details.</p>
                        <Row className="g-3">
                          <Col xs={4} md={2}>
                            <Form.Group className="mb-3">
                              <Form.Label>Title</Form.Label>
                              <Form.Select {...ahField("title")}>
                                <option value=""></option>
                                {["Mr", "Mrs", "Ms", "Dr", "Prof"].map((t) => <option key={t}>{t}</option>)}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col xs={8} md={5}>
                            <Form.Group className="mb-3">
                              <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                              <Form.Control {...ahField("firstName")} placeholder="First name" />
                              {errors["accountHolder.firstName"] && <div className="text-danger small mt-1">{errors["accountHolder.firstName"]}</div>}
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={5}>
                            <Form.Group className="mb-3">
                              <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                              <Form.Control {...ahField("lastName")} placeholder="Last name" />
                              {errors["accountHolder.lastName"] && <div className="text-danger small mt-1">{errors["accountHolder.lastName"]}</div>}
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="g-3">
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>ID Number</Form.Label>
                              <Form.Control {...ahField("idNumber")} placeholder="13-digit SA ID" maxLength={13} />
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Date of Birth</Form.Label>
                              <Form.Control type="date" {...ahField("dob")} />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="g-3">
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Phone Number <span className="text-danger">*</span></Form.Label>
                              <Form.Control {...ahField("tel")} type="tel" />
                              {errors["accountHolder.tel"] && <div className="text-danger small mt-1">{errors["accountHolder.tel"]}</div>}
                            </Form.Group>
                          </Col>
                          <Col xs={12} md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Email Address</Form.Label>
                              <Form.Control {...ahField("email")} type="email" />
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}

                    <Form.Group className="mb-2">
                      <Form.Label>Home Address <span className="text-danger">*</span></Form.Label>
                      <Form.Control {...ahField("address1")} placeholder="Address line 1" className="mb-2" isInvalid={!!errors["accountHolder.address1"]} />
                      {errors["accountHolder.address1"] && <div className="text-danger small mb-2">{errors["accountHolder.address1"]}</div>}
                      <Form.Control {...ahField("address2")} placeholder="Address line 2 (optional)" className="mb-2" />
                      <Row className="g-3">
                        <Col xs={12} md={5}>
                          <Form.Control {...ahField("city")} placeholder="City" className="mb-2" />
                        </Col>
                        <Col xs={8} md={4}>
                          <Form.Control {...ahField("province")} placeholder="Province" className="mb-2" />
                        </Col>
                        <Col xs={4} md={3}>
                          <Form.Control {...ahField("postalCode")} placeholder="Postal code" className="mb-2" />
                        </Col>
                      </Row>
                    </Form.Group>
                  </Card.Body>
                </Card>
              )}

              {/* Flow C: main member identifiers */}
              {flowKey === "C" && (
                <Card className="shadow-sm mb-3">
                  <Card.Body>
                    <Card.Title className="mb-1">Main Member Details</Card.Title>
                    <p className="text-muted small mb-4">
                      Please provide the main member's details on the medical aid.
                    </p>
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control {...mmField("firstName")} placeholder="First name" />
                          {errors["mainMember.firstName"] && <div className="text-danger small mt-1">{errors["mainMember.firstName"]}</div>}
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control {...mmField("lastName")} placeholder="Last name" />
                          {errors["mainMember.lastName"] && <div className="text-danger small mt-1">{errors["mainMember.lastName"]}</div>}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>ID Number</Form.Label>
                          <Form.Control {...mmField("idNumber")} placeholder="13-digit SA ID" maxLength={13} />
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone Number</Form.Label>
                          <Form.Control {...mmField("tel")} type="tel" />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Flow D: account holder identifiers */}
              {flowKey === "D" && (
                <Card className="shadow-sm mb-3">
                  <Card.Body>
                    <Card.Title className="mb-1">Account Holder Details</Card.Title>
                    <p className="text-muted small mb-4">
                      Please provide the account holder's details so we can locate your existing account.
                    </p>
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>First Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control {...ahField("firstName")} placeholder="First name" />
                          {errors["accountHolder.firstName"] && <div className="text-danger small mt-1">{errors["accountHolder.firstName"]}</div>}
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Last Name <span className="text-danger">*</span></Form.Label>
                          <Form.Control {...ahField("lastName")} placeholder="Last name" />
                          {errors["accountHolder.lastName"] && <div className="text-danger small mt-1">{errors["accountHolder.lastName"]}</div>}
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row className="g-3">
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Date of Birth <span className="text-danger">*</span></Form.Label>
                          <Form.Control type="date" {...ahField("dob")} />
                          {errors["accountHolder.dob"] && <div className="text-danger small mt-1">{errors["accountHolder.dob"]}</div>}
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>ID Number</Form.Label>
                          <Form.Control {...ahField("idNumber")} placeholder="13-digit SA ID" maxLength={13} />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* INTAKE TEMPLATE STEPS — one card per page per attached template  */}
          {/* (Inserted between account-holder and review.)                    */}
          {/* ---------------------------------------------------------------- */}
          {currentKind === "template-page" && steps[stepIndex] && (
            <Card className="shadow-sm mb-3">
              <Card.Body>
                <Card.Title className="mb-4">{steps[stepIndex].label}</Card.Title>
                <NoteRenderer
                  mode="fill"
                  pages={[steps[stepIndex].page]}
                  values={templateCaptures[steps[stepIndex].templateId]?.capturedValues || {}}
                  onChange={(elementId, value) =>
                    setTemplateCaptures((prev) => {
                      const tplId = steps[stepIndex].templateId;
                      const existing = prev[tplId]?.capturedValues || {};
                      return {
                        ...prev,
                        [tplId]: {
                          capturedValues: { ...existing, [elementId]: value },
                        },
                      };
                    })
                  }
                />
              </Card.Body>
            </Card>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* STEP 5: Review & submit                                          */}
          {/* ---------------------------------------------------------------- */}
          {step === 5 && (
            <Card className="shadow-sm mb-3">
              <Card.Body>
                <Card.Title className="mb-4">Review & Submit</Card.Title>
                <Alert variant="info" className="small">
                  Please review and agree to the terms and conditions before submitting.
                </Alert>

                {termsAndConditions && (
                  <div className="mb-4">
                    <div className="fw-semibold mb-2">Terms &amp; Conditions</div>
                    <div
                      className="border rounded p-3 bg-light small"
                      style={{ maxHeight: 220, overflowY: "auto" }}
                      dangerouslySetInnerHTML={{ __html: termsAndConditions }}
                    />
                    <Form.Check
                      type="checkbox"
                      id="consentAgreed"
                      className="mt-3"
                      label="I have read and agree to the terms and conditions above"
                      checked={consentAgreed}
                      onChange={(e) => setConsentAgreed(e.target.checked)}
                    />
                    <Form.Check
                      type="checkbox"
                      id="isProxy"
                      className="mt-2"
                      label="I am signing on behalf of the patient (e.g. parent / guardian of a minor)"
                      checked={isProxy}
                      onChange={(e) => setIsProxy(e.target.checked)}
                    />
                    {isProxy && (
                      <Form.Control
                        className="mt-2"
                        placeholder="Describe your relationship to the patient (e.g. Mother of minor)"
                        value={proxyNote}
                        onChange={(e) => setProxyNote(e.target.value)}
                        maxLength={200}
                      />
                    )}
                  </div>
                )}

                <Button
                  variant="success"
                  size="lg"
                  className="w-25"
                  onClick={handleSubmit}
                  disabled={submitting || (!!termsAndConditions && !consentAgreed)}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Submitting...
                    </>
                  ) : (
                    "Submit My Details"
                  )}
                </Button>
              </Card.Body>
            </Card>
          )}

        </Form>

        {/* Inline navigation buttons */}
        {currentKind !== "review" && (
  <div className="d-flex align-items-center gap-3 mb-4">
    {stepIndex > 0 && (
      <Button
        variant="outline-secondary"
        onClick={goBack}
        className="w-25"
        style={{ minHeight: 48 }}
      >
        Back
      </Button>
    )}

    <Button
      variant="primary"
      onClick={goNext}
      disabled={preflightLoading}
      className="w-25 ms-auto"
      style={{ minHeight: 48 }}
    >
      {preflightLoading ? (
        <>
          <Spinner as="span" size="sm" animation="border" className="me-2" />
          Please wait…
        </>
      ) : steps[stepIndex + 1]?.kind === "review" ? (
        "Review"
      ) : (
        "Next"
      )}
    </Button>
  </div>
)}
      </Container>

      {/* Footer — PractoSync branding (fixed) */}
      {!embedded && (
        <div
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0,
            backgroundColor: "#fff", borderTop: "1px solid #dee2e6",
            padding: "10px 16px", display: "flex", alignItems: "center", gap: 10,
            zIndex: 100,
          }}
        >
          <Image
            src="https://practostorage.blob.core.windows.net/practopublic/logo.png"
            alt="PractoSync"
            style={{ maxHeight: 30 }}
          />
          <small className="text-muted">Powered by PractoSync</small>
        </div>
      )}
    </div>
  );
}
